import { FunctionComponent, useEffect } from 'react';
import styles from "./CampaignsForm.module.css";
import cn from 'classnames';
import {Card} from 'react-bootstrap';
import FormField, { IFieldTheme, IFieldType } from '../../common/FormField/FormField';
import FormTextArea from '../../common/FormField/FormTextArea';
import FormCheckbox from '../../common/FormField/FormCheckbox';
import CustomButton, { ButtonType } from '../../common/CustomButton/CustomButton';
import { RootState, AppDispatch } from '../../../redux/store';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { IAddCampaignData, ICampaignData } from '../../../redux/slices/campaigns/types';
import { addCampaign, updateCampaign } from '../../../redux/slices/campaigns/actions';
import { CampaignType } from '../../../constant/enums';
import FormSelect from '../../common/FormField/FormSelect';
import { toast } from 'react-toastify';
import { clearMessage, clearError  } from '../../../redux/slices/campaigns/slice';
import { format } from 'date-fns';

type FormMode = 'create' | 'view' | 'edit' | 'clone';

interface ICampaignsFormProps {
    className?: string;
    campaign?: ICampaignData | null;
    mode: FormMode;
    onCancel: () => void;
    onSuccess: () => void;
};

export const campaignTypeOptions= [
    {value: "catch", label: "Catch Data"},
    {value: "invite", label: "Invite Friends"},
];

const today = new Date();
today.setHours(0, 0, 0, 0);

const getValidationSchema = (mode: FormMode) => Yup.object().shape({
    title: Yup.string().trim().required('Title is required'),
    campaignType: Yup.string().required('Campaign type is required'),
    startDate: mode === 'edit'
        ? Yup.date().required('Start date is required')
        : Yup.date()
            .required('Start date is required')
            .min(today, 'Start date cannot be in the past'),
    endDate: Yup.date().nullable()
        .test(
            'end-date-after-start-date',
            'End date cannot be earlier than start date',
            function (endDate) {
                const { startDate } = this.parent;
                if (!startDate || !endDate)  return true;
                const start = new Date(startDate);
                const end = new Date(endDate);
                start.setHours(0, 0, 0, 0);
                end.setHours(0, 0, 0, 0);
                return end >= start;
            }
        ),
    triggerRule: Yup.number().required('Trigger rule is required'),
    reminderFrequency: Yup.number().required('Reminder frequency is required'),
    message: Yup.string().trim().required('Message is required'),
    inApp: Yup.boolean().test('one-of-three', 'At least one notification channel must be selected', function() {
        return this.parent.inApp || this.parent.email || this.parent.sms;
    }),
    email: Yup.boolean(),
    sms: Yup.boolean(),
});

const CampaignsForm: FunctionComponent<ICampaignsFormProps> = ({className, mode, campaign, onCancel, onSuccess }) => {
    const dispatch= useDispatch<AppDispatch>();
    const {  campaignAdding, addCampaignError, addCampaignMessage, campaignUpdating, updateCampaignError, updateCampaignMessage } = useSelector((state: RootState) => state.campaigns);
    const isEditable = mode !== 'view';
    const validationSchema= getValidationSchema(mode);
    
    const formik= useFormik({
        initialValues: {
            title: '',
            campaignType: '',
            startDate: '',
            endDate: '',
            triggerRule: '',
            reminderFrequency: '',
            message: '',
            inApp: false,
            email: false,
            sms: false,
        },
        validationSchema,
        onSubmit: (values, { resetForm }) => {
            if (!isEditable) return;
            const payload: IAddCampaignData = {
                name: values.title.trim(),
                message: values.message.trim(),
                campaign_type: values.campaignType as CampaignType,
                trigger_rule: Number(values.triggerRule) || 0,
                frequency: Number(values.reminderFrequency) || 0,
                start_date: values.startDate,
                end_date: values.endDate || undefined,
                in_app: values.inApp,
                email: values.email,
                sms: values.sms,
            }

            const handleSuccess = () => {
                resetForm();
                onSuccess();
            };

            if (mode === 'edit' && campaign) {
                dispatch(updateCampaign({ id: campaign.id, data: payload })).unwrap().then(handleSuccess).catch(() => console.error('Error in updating campaign'));
            } else { // 'create' or 'clone'
                dispatch(addCampaign(payload)).unwrap().then(handleSuccess).catch(() => console.error('Error in adding campaign'));
            }
        }
    })

    useEffect(()=> {
        if (campaign) {
            formik.setValues({
                title: campaign.name || '',
                campaignType: campaign.campaign_type || '',
                startDate: campaign.start_date ? format(new Date(campaign.start_date), 'yyyy-MM-dd') : '',
                endDate: campaign.end_date ? format(new Date(campaign.end_date), 'yyyy-MM-dd') : '',
                triggerRule: String(campaign.trigger_rule) || '',
                reminderFrequency: String(campaign.frequency) || '',
                message: campaign.message || '',
                inApp: campaign.in_app || false,
                email: campaign.email || false,
                sms: campaign.sms || false,
            });
        } else {
            formik.resetForm();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaign]);

    useEffect(()=> {
        const error = addCampaignError || updateCampaignError;
        const message = addCampaignMessage || updateCampaignMessage;
        if(error){
            toast.error(error);
            dispatch(clearError());
        }
        if(message){
            toast.success(message);
            dispatch(clearMessage());
        }
    }, [dispatch, addCampaignMessage, addCampaignError, updateCampaignMessage, updateCampaignError])

    const isLoading = campaignAdding === 'pending' || campaignUpdating === 'pending';
    const getButtonText = () => {
        if (isLoading) {
            return mode === 'edit' ? 'Updating...' : 'Saving...';
        }
        switch (mode) {
            case 'edit':
                return 'Update Campaign';
            case 'clone':
                return 'Clone Campaign';
            default:
                return 'Add Campaign';
        }
    }

    const getTriggerRuleText = () => {
        switch (formik.values.campaignType) {
            case CampaignType.INVITE:
                return "Angler hasn't invited friends from last";
            case CampaignType.CATCH:
            default:
                return "Angler hasn't uploaded catch data from last";
        }
    }

    const getMessagePlaceholderText = () => {
        switch (formik.values.campaignType) {
            case CampaignType.INVITE:
                return "Hey Anglers! looks like your friends list is a little empty! Let's invite some new fishing buddies.";
            case CampaignType.CATCH:
            default:
                return `Hey Anglers! Don’t forget to log your latest catch and keep your fishing record up to date.`;
        }
    }

    return (
        <Card className={cn(styles.addNewCampaignForm, className)}>
            <form onSubmit={formik.handleSubmit}>
                <div className={cn(styles.formArea)}>
                    <div className={styles.formDetail}>
                        <div className={styles.formDetailRowOne}>
                            <div className={styles.title}>
                                {mode === 'create' && 'New Campaign'}
                                {mode === 'view' && 'View Campaign'}
                                {mode === 'edit' && 'Edit Campaign'}
                                {mode === 'clone' && 'Clone Campaign'}
                            </div>
                            <div className={styles.formFieldWrap}>
                                <div className={styles.inputText}>
                                    <FormField
                                        label='Campaign Title*'
                                        fieldType={IFieldType.TEXT} 
                                        fieldTheme={IFieldTheme.LIGHT}
                                        placeholder='Enter a title'
                                        {...formik.getFieldProps('title')}
                                        errorText={formik.errors.title}
                                        showError={!!(formik.touched.title && formik.errors.title)}
                                        disabled={!isEditable}
                                    />
                                    <FormSelect
                                        label='Campaign Type*'
                                        fieldTheme={IFieldTheme.LIGHT}
                                        placeholder='Select campaign type'
                                        {...formik.getFieldProps('campaignType')}
                                        errorText={formik.errors.campaignType}
                                        showError={!!(formik.touched.campaignType && formik.errors.campaignType)}
                                        disabled={!isEditable}
                                    >
                                    <option value="" disabled>Select type</option>
                                        {campaignTypeOptions.map((option)=> <option key={option.value} value={option.value}>{option.label}</option>)}
                                    </FormSelect>
                                </div>
                                <div className={styles.inputDateWrap}>
                                    <FormField
                                        label='Start Date*'
                                        fieldType={IFieldType.DATE} 
                                        fieldTheme={IFieldTheme.LIGHT}
                                        className={styles.date}
                                        placeholder='Select start date'
                                        {...formik.getFieldProps('startDate')}
                                        errorText={formik.errors.startDate}
                                        showError={!!(formik.touched.startDate && formik.errors.startDate)}
                                        disabled={!isEditable}
                                    />
                                    <FormField
                                        label='End Date (Optional)'
                                        fieldType={IFieldType.DATE} 
                                        fieldTheme={IFieldTheme.LIGHT}
                                        className={styles.date}
                                        placeholder='Select end date'
                                        {...formik.getFieldProps('endDate')}
                                        errorText={formik.errors.endDate}
                                        showError={!!(formik.touched.endDate && formik.errors.endDate)}
                                        disabled={!isEditable}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className={styles.formDetailRowTwo}>
                            <Card.Title as={'div'} bsPrefix={styles.subtitle}>Reminder Criteria</Card.Title>
                            <div className={styles.criteriaWrap}>
                                <div className={styles.criteriaText}>
                                    <span className={styles.criteriaBold}>Trigger Rule*:</span> {getTriggerRuleText()}
                                </div>
                                <div className={styles.criteriaInput}>
                                    <FormField
                                        fieldType={IFieldType.NUMBER} 
                                        fieldTheme={IFieldTheme.LIGHT}
                                        className={styles.getNumber}
                                        {...formik.getFieldProps('triggerRule')}
                                        errorText={formik.errors.triggerRule}
                                        showError={!!(formik.touched.triggerRule && formik.errors.triggerRule)}
                                        disabled={!isEditable}
                                    />
                                    <div className={styles.criteriaInputText}>days</div>
                                </div>
                            </div>
                            <div className={styles.criteriaWrap}>
                                <div className={styles.criteriaText}>
                                    <span className={styles.criteriaBold}>Reminder Frequency*:</span>  Repeat every
                                </div>
                                <div className={styles.criteriaInput}>
                                    <FormField
                                        fieldType={IFieldType.NUMBER} 
                                        fieldTheme={IFieldTheme.LIGHT}
                                        className={styles.getNumber}
                                        {...formik.getFieldProps('reminderFrequency')}
                                        errorText={formik.errors.reminderFrequency}
                                        showError={!!(formik.touched.reminderFrequency && formik.errors.reminderFrequency)}
                                        disabled={!isEditable}
                                    />
                                    <div className={styles.criteriaInputText}>days until updated</div>
                                </div>
                            </div>
                        </div>
                        <div className={styles.formDetailRowThree}>
                            <Card.Title as={'div'} bsPrefix={styles.subtitle}>Preview Message*</Card.Title>
                            <FormTextArea
                                fieldTheme={IFieldTheme.LIGHT}
                                placeholder={getMessagePlaceholderText()}
                                {...formik.getFieldProps('message')}
                                errorText={formik.errors.message}
                                showError={!!(formik.touched.message && formik.errors.message)}
                                disabled={!isEditable}
                            />
                        </div>
                    </div>
                    <div className={styles.checkboxesWrap}>
                        <Card.Title as={'div'} bsPrefix={styles.subtitle}>Notification Channels* </Card.Title>
                        <div className={styles.checkboxes}>
                            <FormCheckbox label={'In App'} {...formik.getFieldProps('inApp')} checked={formik.values.inApp} disabled={!isEditable} />
                            <FormCheckbox label={'Email'} {...formik.getFieldProps('email')} checked={formik.values.email} disabled={!isEditable} />
                            <FormCheckbox label={'SMS'} {...formik.getFieldProps('sms')} checked={formik.values.sms} disabled={!isEditable} />
                        </div>
                        {formik.errors.inApp && (formik.touched.inApp || formik.touched.email || formik.touched.sms) ? <div style={{ color: '#dc3545', marginTop: '4px', fontSize: '12px' }}>{formik.errors.inApp}</div> : null}
                    </div>
                </div>
                <div className={cn(styles.formOption)}>
                    {isEditable ? (
                        <>
                            <CustomButton 
                                className={styles.addNewContentBtn}
                                btnType={ButtonType.LIGHT}
                                type='button'
                                onClick={onCancel}
                            >
                                Cancel
                            </CustomButton>
                            <CustomButton 
                                className={styles.addNewContentBtn}
                                btnType={ButtonType.GREEN}
                                type='submit'
                                disabled={isLoading}
                            >
                                {getButtonText()}
                            </CustomButton>
                        </>
                    ) : (
                        <CustomButton 
                            className={styles.addNewContentBtn}
                            btnType={ButtonType.LIGHT}
                            type='button'
                            onClick={onCancel}
                        >
                            Close
                        </CustomButton>
                    )}
                </div>
            </form>
        </Card>
    );
};

export default CampaignsForm;
